import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import { CheckCircle, Warning, Info, HourglassEmpty, Cancel, Category } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { holidaysAPI } from '../services/api';
import Swal from 'sweetalert2';

const EmployeeHolidays = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categorySelections, setCategorySelections] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, [year]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load category selections
      const response = await holidaysAPI.getMyCategorySelections(year);
      
      if (response.data.success) {
        setCategorySelections(response.data.data);
      }
    } catch (err) {
      console.error('Error loading category selections:', err);
      setError('Failed to load holiday selections');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Chip 
            icon={<CheckCircle />} 
            label="Approved" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(76, 175, 80, 0.2)', 
              color: '#81C784',
              border: '1px solid #81C784'
            }} 
          />
        );
      case 'pending':
        return (
          <Chip 
            icon={<HourglassEmpty />} 
            label="Pending" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(255, 152, 0, 0.2)', 
              color: '#FFB74D',
              border: '1px solid #FFB74D'
            }} 
          />
        );
      case 'rejected':
        return (
          <Chip 
            icon={<Cancel />} 
            label="Rejected" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(244, 67, 54, 0.2)', 
              color: '#EF9A9A',
              border: '1px solid #EF9A9A'
            }} 
          />
        );
      default:
        return null;
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'A': return '#2196F3';
      case 'B': return '#4CAF50';
      case 'C': return '#FF9800';
      default: return '#D4AF37';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F5F0E8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            <Category sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
            My Holiday Selections - {year}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Category />}
            onClick={() => window.location.href = '/holiday-categories'}
            sx={{ 
              borderColor: '#D4AF37', 
              color: '#D4AF37',
              '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' }
            }}
          >
            Select Category Holidays
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', color: '#EF5350', border: '1px solid #EF5350' }} 
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2, bgcolor: 'rgb(10, 25, 41)', color: '#81C784', border: '1px solid #81C784' }} 
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        {/* Category Selections */}
        {categorySelections ? (
          <Box>
            {['A', 'B', 'C'].map(cat => {
              const catData = categorySelections[cat];
              if (!catData || catData.holidays.length === 0) return null;
              
              return (
                <Card key={cat} sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: `2px solid ${getCategoryColor(cat)}`, borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: getCategoryColor(cat), fontWeight: 600 }}>
                        Category {cat}: {catData.selected} selected 
                        <Typography component="span" sx={{ fontSize: '0.875rem', color: '#B4C4A8', fontWeight: 400 }}>
                          (Min: {catData.min}, Max: {catData.max})
                        </Typography>
                      </Typography>
                    </Box>
                    
                    <List dense>
                      {catData.holidays.map(h => (
                        <ListItem key={h.id} sx={{ bgcolor: h.status === 'approved' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.05)', borderRadius: 1, mb: 0.5 }}>
                          <ListItemIcon>
                            {h.status === 'approved' ? (
                              <CheckCircle sx={{ color: '#81C784' }} />
                            ) : h.status === 'pending' ? (
                              <HourglassEmpty sx={{ color: '#FFB74D' }} />
                            ) : (
                              <Cancel sx={{ color: '#EF9A9A' }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography sx={{ color: '#FFFFFF' }}>{h.name}</Typography>}
                            secondary={<Typography sx={{ color: '#B4C4A8' }}>{h.date}</Typography>}
                          />
                          {getStatusChip(h.status)}
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#B4C4A8' }}>
                No holiday selections found for {year}. Please select holidays from categories.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Category />}
                onClick={() => window.location.href = '/holiday-categories'}
                sx={{ 
                  mt: 2, 
                  bgcolor: '#D4AF37', 
                  color: 'rgb(10, 25, 41)',
                  '&:hover': { bgcolor: '#C19B2E' }
                }}
              >
                Go to Holiday Categories
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeHolidays;
